<template>
    <div class="relative">
        <div v-if="computedType === 'select-search'" class="relative">
            <input
                v-model="search"
                :name="name"
                :id="id"
                :class="inputElClass"
                :placeholder="placeholder"
                :disabled="disabled"
                type="text"
                autocomplete="off"
                @focus="openSelectSearch"
                @click="openSelectSearch"
                @input="isSelectSearchEditing = true"
                @blur="onSelectSearchBlur"
            >

            <div
                v-if="isSelectSearchOpen && filteredSelectOptions.length"
                class="autocomplete-options border border-gray-700 rounded overflow-hidden"
            >
            <span
                class="autocomplete-options__option hover:bg-blue-600 hover:text-white p-2"
                v-for="option in filteredSelectOptions"
                :key="option.id ?? option"
                @click="selectSearchOption(option)"
            >
                {{ option.label ?? option }}
            </span>
            </div>
        </div>
        <select
            v-else-if="computedType === 'select'"
            v-model="computedValue"
            :name="name"
            :id="id"
            :class="inputElClass"
            :disabled="disabled">
            <option value="" disabled selected hidden>{{ placeholder }}</option>
            <option v-for="option in options" :key="option.id ?? option" :value="option">{{
                    option.label ?? option
                }}
            </option>
        </select>
        <textarea
            v-else-if="computedType === 'textarea'"
            v-model="computedValue"
            :class="inputElClass"
            :name="name"
            :id="id"
            :placeholder="placeholder"
            :required="required"
        ></textarea>
        <input
            v-else
            v-model="computedValue"
            :name="name"
            :autocomplete="autocomplete"
            :required="required"
            :id="id"
            :placeholder="placeholder"
            :type="computedType"
            :class="inputElClass"
            :disabled="disabled"
        >
        <control-icon
            v-if="icon"
            :icon="icon"
            :h="controlIconH"
        />
        <icon
            v-if="isLoading"
            :path="mdiLoading"
            w="w-10"
            :h="controlIconH"
            class="animate-spin absolute top-0 right-0 z-10 pointer-events-none text-gray-500"
        />
        <div class="autocomplete-options border border-gray-700 rounded overflow-hidden"
             v-if="typeof computedOptions === 'object' && computedOptions.length > 0">
            <span class="autocomplete-options__option hover:bg-blue-600 hover:text-white p-2"
                  @click="selectAutocompleteOption(autocompleteOption)"
                  v-for="autocompleteOption in computedOptions" :key="autocompleteOption.id ?? autocompleteOption"
                  :value="autocompleteOption">
                {{ autocompleteOption[autocompleteLabel] ?? autocompleteOption }}
            </span>
        </div>
    </div>
</template>

<style>
.autocomplete-options {
    width: 100%;
    max-height: 240px;
    overflow-y: auto;

    background-color: white;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 99;
}

.autocomplete-options__option {
    cursor: pointer;
    display: block;
    width: 100%;
}
</style>

<script>
import {computed, ref, watch} from 'vue'
import ControlIcon from '@/Components/Dashboard/ControlIcon'
import Icon from '@/Components/Dashboard/Icon'
import {mdiLoading} from '@mdi/js';

export default {
    name: 'Control',
    components: {
        ControlIcon,
        Icon
    },
    props: {
        name: String,
        id: String,
        required: Boolean,
        autocomplete: String,
        placeholder: String,
        icon: String,
        options: Array,
        autocompleteOptions: Array,
        autocompleteLabel: {
            type: String,
            default: 'label'
        },
        autocompleteFunction: Function,
        type: {
            type: String,
            default: 'text'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        modelValue: {
            type: [String, Number, Boolean, Array, Object],
            default: ''
        }
    },
    emits: ['update:modelValue', 'update:autocompleteOptions', 'right-icon-click', 'autocompleteOptionSelect'],
    setup(props, {emit}) {
        const computedValue = computed({
            get: () => props.modelValue,
            set: value => {
                emit('update:modelValue', value)
            }
        })

        const computedOptions = computed({
            get: () => props.autocompleteOptions,
            set: value => {
                emit('update:autocompleteOptions', value)
            }
        })

        const inputElClass = computed(() => {
            const base = [
                'px-3 py-2 max-w-full focus:ring focus:outline-none border border-gray-700 rounded w-full',
                computedType.value === 'textarea' ? 'h-24' : 'h-12'
            ]

            if (props.icon) {
                base.push('pl-10')
            }

            return base
        })

        const computedType = computed(() => props.options && props.type !== 'select-search' ? 'select' : props.type)

        const controlIconH = computed(() => props.type === 'textarea' ? 'h-full' : 'h-12')

        const search = ref('')

        const isSelectSearchEditing = ref(false)

        const isSelectSearchOpen = ref(false)

        const filteredSelectOptions = computed(() => {
            if (!props.options) {
                return []
            }

            if (!isSelectSearchEditing.value || !search.value) {
                return props.options
            }

            return props.options.filter(option => {
                const label = String(option.label ?? option).toLowerCase()

                return label.includes(search.value.toLowerCase())
            })
        })

        watch(
            () => props.modelValue,
                value => {
                    if (props.type !== 'select-search') {
                        return
                    }

                search.value = value?.label ?? value ?? ''
            },
            { immediate: true }
        )

        return {
            mdiLoading,
            computedValue,
            inputElClass,
            computedType,
            controlIconH,
            computedOptions,
            isSelectSearchEditing,
            search,
            isSelectSearchOpen,
            filteredSelectOptions
        }
    },
    methods: {
        selectAutocompleteOption(autocompleteOption) {
            this.computedValue = autocompleteOption[this.autocompleteLabel] ?? autocompleteOption
            this.$emit('autocompleteOptionSelect', autocompleteOption)
        },

        openSelectSearch() {
            this.isSelectSearchOpen = true
            this.isSelectSearchEditing = false
        },

        selectSearchOption(option) {
            this.computedValue = option
            this.search = option.label ?? option
            this.isSelectSearchOpen = false
            this.isSelectSearchEditing = false
        },

        onSelectSearchBlur() {
            setTimeout(() => {
                this.isSelectSearchOpen = false
                this.isSelectSearchEditing = false
            }, 150)
        },
    }
}
</script>
